import type { AxiosError, AxiosResponse } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import { Blueprint } from '@/types/blueprint';
import { Item, ItemParams, ItemPayload } from '@/types/item';
import PaginatedResponse from '@/types/paginatedResponse';
import RepositoryResponse from '@/types/repositoryResponse';
import { WorkGroup } from '@/types/workGroup';
import ajax from '@/utils/ajax';
import defaultRepositoryError from '@/utils/defaultRepositoryError';

export const workRepository = {
  async createItem({
    host,
    item
  }: {
    host: string;
    item: Partial<ItemPayload>;
  }): Promise<RepositoryResponse<Item>> {
    try {
      const path = '/api/work/items/';
      const response = await ajax.post<Partial<ItemPayload>, AxiosResponse<Item>>({
        url: `${host}${path}`,
        data: item
      });

      return {
        data: response.data as Item,
        isError: false
      };
    } catch (error) {
      return defaultRepositoryError(error as AxiosError, 'Failed to create task.');
    }
  },

  async getItem({
    host,
    itemId
  }: {
    host: string;
    itemId: string;
  }): Promise<RepositoryResponse<Item>> {
    try {
      const path = `/api/work/items/${itemId}/`;
      const response = await ajax.get({
        url: `${host}${path}`
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      return defaultRepositoryError(error as AxiosError, 'Failed to get task data.');
    }
  },

  async getItemByKustomerEventId({
    host,
    kustomerEventId
  }: {
    host: string;
    kustomerEventId: string;
  }): Promise<RepositoryResponse<Item | { id: null }>> {
    try {
      const path = `/api/work/items/by_kustomer_conversation_id/${kustomerEventId}/`;
      const response = await ajax.get({
        url: `${host}${path}`
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      return defaultRepositoryError(error as AxiosError, 'Failed to get task data.');
    }
  },

  async getItems({
    host,
    taskableTypeModel,
    taskableId,
    personId,
    params
  }: {
    host: string;
    taskableTypeModel?: string | string[];
    taskableId?: string | string[];
    personId?: string;
    params?: ItemParams;
  }): Promise<RepositoryResponse<PaginatedResponse<Item[]>>> {
    try {
      const path = `/api/work/items/`;
      const response = await ajax.get({
        url: `${host}${path}`,
        params: {
          taskable_type__model: taskableTypeModel,
          taskable_id: taskableId,
          person_id: personId,
          ...params
        }
      });

      return {
        data: response.data as PaginatedResponse<Item[]>,
        isError: false
      };
    } catch (error) {
      return defaultRepositoryError(error as AxiosError, 'Failed to get tasks.');
    }
  },

  async previewItem({
    host,
    taskableId,
    taskableTypeName,
    blueprintId
  }: {
    host: string;
    taskableId?: string;
    taskableTypeName?: string;
    blueprintId?: string;
  }): Promise<RepositoryResponse<Item>> {
    if (!taskableId || !taskableTypeName || !blueprintId)
      return Promise.reject(
        new Error(`Invalid params ${taskableId} ${taskableTypeName} ${blueprintId}}`)
      );
    try {
      const path = `/api/work/items/preview/`;
      const response = await ajax.post<Partial<Item>, RepositoryResponse<Item>>({
        url: `${host}${path}`,
        data: {
          taskable_id: taskableId,
          taskable_type: taskableTypeName,
          blueprint_id: blueprintId
        }
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      return defaultRepositoryError(error as AxiosError, 'Failed to get task data.');
    }
  },

  async updateItem({
    host,
    itemId,
    item
  }: {
    host: string;
    itemId: string;
    item: Partial<ItemPayload>;
  }): Promise<RepositoryResponse<Item>> {
    try {
      const path = `/api/work/items/${itemId}/`;
      const response = await ajax.patch<Partial<ItemPayload>, AxiosResponse<Item>>({
        url: `${host}${path}`,
        data: item
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      return defaultRepositoryError(error as AxiosError, 'Failed to update task.');
    }
  },

  async deleteItem({
    host,
    itemId
  }: {
    host: string;
    itemId: string;
  }): Promise<RepositoryResponse<void>> {
    try {
      const path = `/api/work/items/${itemId}/`;
      await ajax.delete({
        url: `${host}${path}`,
        data: {}
      });

      return {
        isError: false
      };
    } catch (error) {
      return defaultRepositoryError(error as AxiosError, 'Failed to delete task.');
    }
  },

  async listBlueprints({
    host,
    taskableTypeModel
  }: {
    host: string;
    taskableTypeModel?: string;
  }): Promise<RepositoryResponse<Blueprint[]>> {
    try {
      const path = '/api/work/blueprints/';
      const response = await ajax.get({
        url: `${host}${path}`,
        params: {
          taskable_type__model: taskableTypeModel,
          // Passing size=0 to bypass pagination
          size: 0
        }
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      return defaultRepositoryError(error as AxiosError, 'Failed to get blueprints.');
    }
  },

  async getBlueprint({
    host,
    blueprintId
  }: {
    host: string;
    blueprintId: string;
  }): Promise<RepositoryResponse<Blueprint>> {
    try {
      const path = `/api/work/blueprints/${blueprintId}/`;
      const response = await ajax.get({
        url: `${host}${path}`
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      return defaultRepositoryError(error as AxiosError, 'Failed to get blueprint.');
    }
  },

  async updateBlueprintFormSchema({
    host,
    blueprintId,
    formSchema
  }: {
    host: string;
    blueprintId: string;
    formSchema: Blueprint['form_schema'];
  }): Promise<RepositoryResponse<Blueprint>> {
    try {
      const path = `/api/work/blueprints/${blueprintId}/`;
      const response = await ajax.patch<
        { form_schema: Blueprint['form_schema'] },
        AxiosResponse<Blueprint>
      >({
        url: `${host}${path}`,
        data: { form_schema: formSchema }
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      return defaultRepositoryError(error as AxiosError, 'Failed to update blueprint form.');
    }
  },

  async getWorkGroups({
    host
  }: {
    host: string;
  }): Promise<RepositoryResponse<PaginatedResponse<WorkGroup[]>>> {
    try {
      const path = '/api/work/work-groups/';
      const { data } = await ajax.get({
        url: `${host}${path}`
      });

      return {
        data,
        isError: false
      };
    } catch (error) {
      return defaultRepositoryError(error as AxiosError, 'Failed to get work groups.');
    }
  }
};

const WorkRepository = () => {
  const { host } = useContext(AppContext);

  return {
    async createItem(item: Partial<ItemPayload>): Promise<RepositoryResponse<Item>> {
      return workRepository.createItem({ host, item });
    },

    async getItem(itemId: string): Promise<RepositoryResponse<Item>> {
      return workRepository.getItem({ host, itemId });
    },

    async getItemByKustomerEventId(
      kustomerEventId: string
    ): Promise<RepositoryResponse<Item | { id: null }>> {
      return workRepository.getItemByKustomerEventId({ host, kustomerEventId });
    },

    async getItems({
      taskableTypeModel,
      taskableId,
      personId,
      params
    }: {
      taskableTypeModel?: string | string[];
      taskableId?: string | string[];
      personId?: string;
      params: ItemParams;
    }): Promise<RepositoryResponse<PaginatedResponse<Item[]>>> {
      return workRepository.getItems({ host, taskableTypeModel, taskableId, personId, params });
    },

    async previewItem(
      taskableId?: string,
      taskableTypeName?: string,
      blueprintId?: string
    ): Promise<RepositoryResponse<Item>> {
      return workRepository.previewItem({ host, taskableId, taskableTypeName, blueprintId });
    },

    async updateItem(
      itemId: string,
      item: Partial<ItemPayload>
    ): Promise<RepositoryResponse<Item>> {
      return workRepository.updateItem({ host, itemId, item });
    },

    async deleteItem(itemId: string): Promise<RepositoryResponse<void>> {
      return workRepository.deleteItem({ host, itemId });
    },

    async listBlueprints(taskableTypeModel?: string): Promise<RepositoryResponse<Blueprint[]>> {
      return workRepository.listBlueprints({ host, taskableTypeModel });
    },

    async getBlueprint(blueprintId: string): Promise<RepositoryResponse<Blueprint>> {
      return workRepository.getBlueprint({ host, blueprintId });
    },

    async updateBlueprintFormSchema(
      blueprintId: string,
      formSchema: Blueprint['form_schema']
    ): Promise<RepositoryResponse<Blueprint>> {
      return workRepository.updateBlueprintFormSchema({ host, blueprintId, formSchema });
    },

    async getWorkGroups(): Promise<RepositoryResponse<PaginatedResponse<WorkGroup[]>>> {
      return workRepository.getWorkGroups({ host });
    }
  };
};

export default WorkRepository;
